/**
 * Transformation Prompts
 *
 * Text transformation prompts for persona, style, and namespace transforms.
 *
 * @module config/prompts/transformation-prompts
 */
// ═══════════════════════════════════════════════════════════════════════════
// TRANSFORMATION PROMPTS
// ═══════════════════════════════════════════════════════════════════════════
export const TRANSFORMATION_SYSTEM = {
    id: 'TRANSFORMATION_SYSTEM',
    name: 'Transformation System Prompt',
    description: 'Base system prompt for all narrative transformations',
    template: `You are a narrative transformation specialist.
You transform text while preserving specified invariants.
Output ONLY the transformed text with no explanation or commentary.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['builder', 'transformation-service'],
};
export const TRANSFORMATION_PERSONA = {
    id: 'TRANSFORMATION_PERSONA',
    name: 'Persona Transformation',
    description: 'Transform WHO perceives/narrates while preserving WHAT and HOW',
    template: `You are a narrative perspective transformation specialist. Your task is to rewrite the following text through the lens of "{{personaName}}".

PERSONA DEFINITION:
{{personaSystemPrompt}}

═══════════════════════════════════════════════════════════════════════════════
LAYER 1: INVARIANTS (MUST PRESERVE)
═══════════════════════════════════════════════════════════════════════════════
These elements define WHAT happens and HOW it's written. Do not change them.

• PLOT & EVENTS: Every event must happen in the same sequence with same outcomes
• FACTS & ENTITIES: All names, locations, objects, dates, and specific details stay the same
• SETTING & UNIVERSE: The world remains the same (don't shift genres or eras)
• DIALOGUE CONTENT: Keep dialogue meaning intact
• WRITING STYLE: Preserve sentence patterns, vocabulary register, figurative language density
  (Persona changes WHO perceives, not HOW they write)

⚠️ VOCABULARY RULE (CRITICAL):
Keep ALL specific nouns, verbs, names, and key terms from the original.
Only change the FRAMING and PERSPECTIVE, not the vocabulary.
Do not replace "boss" with "supervisor", "email" with "correspondence", etc.

═══════════════════════════════════════════════════════════════════════════════
LAYER 2: PERSONA DIMENSIONS (WHAT YOU MAY CHANGE)
═══════════════════════════════════════════════════════════════════════════════
Persona is a stable epistemic operator - it determines WHO perceives, WHAT counts
as salient, WHAT is taken for granted, and HOW uncertainty is handled.

ONTOLOGICAL FRAMING:
• How the narrator understands the world (orderly vs chaotic, improvable vs fixed)
• What forces the narrator sees as primary (systems vs individuals, fate vs agency)

EPISTEMIC STANCE:
• How the narrator knows things (observation, inference, intuition, authority)
• Certainty level (confident assertions vs hedged observations vs open questions)

ATTENTION & SALIENCE:
• What the narrator notices first and lingers on
• What the narrator treats as background or unremarkable

NORMATIVE FRAMING:
• What the narrator implicitly approves or finds admirable (shown, not stated)
• What provokes the narrator's skepticism or concern

{{lengthGuidance}}

═══════════════════════════════════════════════════════════════════════════════
LAYER 3: PROHIBITIONS (HARD NO - NEVER DO THESE)
═══════════════════════════════════════════════════════════════════════════════

❌ NO STYLE CHANGES: Don't alter sentence length patterns, vocabulary register,
   or figurative language density.

❌ NO NEW FACTS: Don't invent new objects, characters, locations, or details.

❌ NO NARRATOR BIOGRAPHY: Don't add "As a scientist, I..." framing.

❌ NO MORAL SERMONS: Values should be implicit, not stated as lessons.

❌ NO PLATFORM ARTIFACTS: Never add "EDIT:", "Thanks for reading", etc.

❌ NO GENRE SHIFTS: Don't turn narrative into essay or vice versa.

═══════════════════════════════════════════════════════════════════════════════
SOURCE TEXT:
═══════════════════════════════════════════════════════════════════════════════
{{text}}

═══════════════════════════════════════════════════════════════════════════════
OUTPUT:
═══════════════════════════════════════════════════════════════════════════════
Output ONLY the transformed text - no explanations, no thinking process.
Begin directly with the transformed content.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['builder', 'transformation-service'],
};
export const TRANSFORMATION_STYLE = {
    id: 'TRANSFORMATION_STYLE',
    name: 'Style Transformation',
    description: 'Transform HOW the text is written while preserving WHO and WHAT',
    template: `You are a writing style transformation specialist. Your task is to rewrite the following text in "{{styleName}}" style.

STYLE GUIDANCE:
{{stylePrompt}}

═══════════════════════════════════════════════════════════════════════════════
LAYER 1: INVARIANTS (MUST PRESERVE)
═══════════════════════════════════════════════════════════════════════════════

• EVENT ORDER: Every event must happen in the same sequence
• CAUSE/EFFECT: Preserve all causal relationships between events
• DIALOGUE CONTENT: Keep dialogue meaning intact
• CHARACTER KNOWLEDGE: Characters know only what they knew originally
• NARRATIVE VIEWPOINT: {{viewpointHint}} - maintain this perspective throughout
• FACTS & ENTITIES: All names, locations, objects, and details stay the same
• GENRE IDENTITY: The text type remains the same

⚠️ VOCABULARY RULE (CRITICAL):
Keep ALL specific nouns, names, and key terms from the original.
Transform sentence STRUCTURE and hedging style only.
Do not replace "boss" with "supervisor", "email" with "missive", etc.

═══════════════════════════════════════════════════════════════════════════════
LAYER 2: STYLE CHANGES (WHAT YOU MAY CHANGE)
═══════════════════════════════════════════════════════════════════════════════

SENTENCE-LEVEL:
• Sentence length and variation
• Clause complexity
• Lexical register
• Cadence and rhythm

FIGURATIVE LANGUAGE:
• Metaphor and simile frequency (within reason)
• Imagery source domains
• Sound devices (light use)

DISCOURSE-LEVEL:
• Connective tissue
• Rhetorical devices
• Pacing of description

{{lengthGuidance}}

═══════════════════════════════════════════════════════════════════════════════
LAYER 3: PROHIBITIONS (HARD NO)
═══════════════════════════════════════════════════════════════════════════════

❌ NO PLATFORM ARTIFACTS: Never add "EDIT:", "Thanks for reading", etc.
❌ NO NARRATOR IDENTITY SHIFT: Don't turn third-person into first-person.
❌ NO NEW FACTS OR ENTITIES: Don't invent new details.
❌ NO MORAL REFRAMING: Don't change the fundamental tone or meaning.
❌ NO VIEWPOINT MIXING: Maintain consistent perspective throughout.

═══════════════════════════════════════════════════════════════════════════════
SOURCE TEXT:
═══════════════════════════════════════════════════════════════════════════════
{{text}}

═══════════════════════════════════════════════════════════════════════════════
OUTPUT:
═══════════════════════════════════════════════════════════════════════════════
Output ONLY the transformed text - no explanations.
Begin directly with the transformed content.`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['builder', 'transformation-service'],
};
/**
 * Namespace transformation prompts - DEPRECATED
 * @deprecated Namespace transformations lose original meaning. Use persona/style instead.
 */
export const TRANSFORMATION_NAMESPACE_EXTRACT = {
    id: 'TRANSFORMATION_NAMESPACE_EXTRACT',
    name: 'Namespace Structure Extraction (Deprecated)',
    description: 'Extract core structure for namespace transformation - DEPRECATED',
    deprecated: true,
    template: `You are a narrative structure analyst.

Extract the CORE STRUCTURE of this narrative without any universe-specific details:
- Who does what (roles, not names)
- What happens (events, not locations)
- What conflicts arise (tensions, not specifics)
- How things resolve (outcomes, not details)

Preserve the NARRATIVE VOICE and TONE completely.

Source Text:
"""
{{text}}
"""

Core Structure (abstract, universe-neutral):`,
    requirements: {
        capabilities: [],
        temperature: 0.5,
        maxTokens: 4096,
    },
    version: 1,
    usedBy: ['transformation-service'],
};
export const TRANSFORMATION_NAMESPACE_MAP = {
    id: 'TRANSFORMATION_NAMESPACE_MAP',
    name: 'Namespace Mapping (Deprecated)',
    description: 'Map abstract structure to new namespace - DEPRECATED',
    deprecated: true,
    template: `You are a narrative universe mapper.

Map this abstract narrative structure into the "{{namespaceName}}" universe:

{{namespaceContextPrompt}}

MAPPING RULES:
1. Translate roles → appropriate entities in {{namespaceName}}
2. Translate events → equivalent actions in {{namespaceName}}
3. Translate conflicts → analogous tensions in {{namespaceName}}
4. Keep the NARRATIVE VOICE and TONE from the original
5. Use proper {{namespaceName}} terminology and concepts

Abstract Structure:
"""
{{structure}}
"""

Mapped to {{namespaceName}}:`,
    requirements: {
        capabilities: [],
        temperature: 0.5,
        maxTokens: 4096,
    },
    version: 1,
    usedBy: ['transformation-service'],
};
export const TRANSFORMATION_NAMESPACE_RECONSTRUCT = {
    id: 'TRANSFORMATION_NAMESPACE_RECONSTRUCT',
    name: 'Namespace Reconstruction (Deprecated)',
    description: 'Reconstruct full narrative from mapped structure - DEPRECATED',
    deprecated: true,
    template: `You are a narrative reconstruction specialist.

Take this {{namespaceName}}-mapped structure and write it as a complete, engaging narrative.

RECONSTRUCTION RULES:
1. Fully realize the {{namespaceName}} universe with vivid details
2. Maintain the EXACT narrative voice and tone from the mapping
3. Keep the same sentence patterns and paragraph structure
4. Make it feel natural and immersive in {{namespaceName}}
{{lengthGuidance}}

Mapped Structure:
"""
{{mapped}}
"""

Complete Narrative in {{namespaceName}}:`,
    requirements: {
        capabilities: [],
        temperature: 0.6,
        maxTokens: 8192,
    },
    version: 1,
    usedBy: ['transformation-service'],
};
export const TRANSFORMATION_PROMPTS = [
    TRANSFORMATION_SYSTEM,
    TRANSFORMATION_PERSONA,
    TRANSFORMATION_STYLE,
    TRANSFORMATION_NAMESPACE_EXTRACT,
    TRANSFORMATION_NAMESPACE_MAP,
    TRANSFORMATION_NAMESPACE_RECONSTRUCT,
];
//# sourceMappingURL=transformation-prompts.js.map